import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../services/api';
import { HedgehogLogo } from '../components/brand/HedgehogLogo';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { Mail, CheckCircle2, ArrowLeft, AlertCircle } from 'lucide-react';

export const ForgotPasswordView: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await apiClient('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      setIsSubmitted(true);
    } catch (err: any) {
      setError(err?.message || 'Unable to process reset request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cf-bg flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <div className="flex justify-center mb-3">
          <HedgehogLogo size={42} />
        </div>
        <h2 className="text-2xl font-bold tracking-tight text-cf-textPrimary">
          Reset Your Password
        </h2>
        <p className="mt-1 text-xs text-cf-textSecondary">
          Enter your account email to receive a password recovery link
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card className="bg-cf-surface py-8 px-6 border-cf-border shadow-xl rounded-lg sm:px-10">
          {isSubmitted ? (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto">
                <CheckCircle2 size={24} />
              </div>
              <h3 className="text-sm font-semibold text-cf-textPrimary">
                Check your inbox
              </h3>
              <p className="text-xs text-cf-textSecondary leading-relaxed">
                If an account exists for <span className="text-cf-textPrimary font-medium">{email}</span>, a secure password reset link has been dispatched. The link will expire in 1 hour.
              </p>
              <div className="pt-4">
                <Button
                  to="/login"
                  variant="secondary"
                  size="sm"
                  fullWidth
                  leftIcon={<ArrowLeft size={14} />}
                >
                  Return to Sign In
                </Button>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-5 p-3 rounded bg-rose-500/10 border border-rose-500/30 flex items-start gap-2.5 text-xs text-rose-600 dark:text-rose-400 font-medium">
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5 text-rose-600 dark:text-rose-400" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Registered Email Address"
                  size="xs"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  leftIcon={<Mail size={14} />}
                />

                <div className="pt-2">
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    fullWidth
                    disabled={isLoading || !email.trim()}
                    isLoading={isLoading}
                  >
                    Send Recovery Link
                  </Button>
                </div>
              </form>

              <div className="mt-6 pt-5 border-t border-cf-border text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-xs text-cf-textSecondary hover:text-cf-textPrimary transition-colors"
                >
                  <ArrowLeft size={13} />
                  <span>Back to Sign In</span>
                </Link>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
